import { Input } from '@genial/components';
import { Spacing } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import * as Clipboard from 'expo-clipboard';
import { Box, Input as NBInput } from 'native-base';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  NativeSyntheticEvent,
  TextInputChangeEventData,
  TextInputKeyPressEventData,
} from 'react-native';

const tokenDigits = 6;

export namespace TokenNumberInputTypes {
  export interface Props {
    onChangeText?: (text: string) => void;
    isDisabled?: boolean;
    value?: string;
  }
}

export const TokenNumberInput: React.FC<TokenNumberInputTypes.Props> = ({
  value,
  onChangeText,
  isDisabled,
}) => {
  const tokenRefs = useRef(
    Array.from({ length: tokenDigits }, () => React.createRef<typeof NBInput>())
  ).current;
  const blankText = useRef<boolean[]>(tokenRefs.map(() => true));
  const [textValues, setTextValues] = useState<string[]>(tokenRefs.map(() => ''));
  const handleKeyPress = useCallback(
    (e: NativeSyntheticEvent<TextInputKeyPressEventData>, index: number) => {
      if (e.nativeEvent.key === 'Backspace') {
        if (tokenRefs[index - 1] && blankText.current[index]) {
          // @ts-expect-error
          tokenRefs[index - 1].current?.focus();
        }

        setTextValues((currentTextValues) => {
          currentTextValues[index] = '';
          return [...currentTextValues];
        });

        blankText.current[index] = true;
      }
    },
    [tokenRefs]
  );

  const handleChange = useCallback(
    async (e: NativeSyntheticEvent<TextInputChangeEventData>, index: number) => {
      if (e.nativeEvent.text.length === 1) {
        blankText.current[index] = false;
        setTextValues((currentTextValues) => {
          currentTextValues[index] = e.nativeEvent.text;
          return [...currentTextValues];
        });

        if (tokenRefs[index + 1]) {
          // @ts-expect-error
          tokenRefs[index + 1].current?.focus();
        }
      } else if (e.nativeEvent.text.length === tokenDigits) {
        const clipboardContent = await Clipboard.getStringAsync();

        if (clipboardContent.length === tokenDigits && clipboardContent.match(/\d{6}/)) {
          setTextValues(clipboardContent.split(''));
        }
      }
    },
    [tokenRefs]
  );

  useEffect(() => {
    if (value === undefined || value === null) {
      return;
    }

    setTextValues(value.split(''));
  }, [value]);

  useEffect(() => {
    if (onChangeText && textValues.every((currentValue) => currentValue?.length === 1)) {
      onChangeText(textValues.join(''));
    } else {
      onChangeText?.('');
    }
  }, [onChangeText, textValues]);

  const tokenInputs = useMemo(
    () =>
      tokenRefs.map((tokenRef, index) => (
        <Box key={index.toString()} flex={1}>
          <Input
            autoComplete="off"
            keyboardType="number-pad"
            variant="filled"
            name={`tokenDigit_${index}`}
            onKeyPress={(e) => handleKeyPress(e, index)}
            onChange={(e) => handleChange(e, index)}
            ref={tokenRef}
            hasShadow={false}
            value={textValues[index]}
            textAlign="center"
            flex={1}
            isDisabled={isDisabled}
          />
        </Box>
      )),
    [handleChange, handleKeyPress, isDisabled, textValues, tokenRefs]
  );

  return (
    <Box
      flexDirection="row"
      flex={1}
      width="100%"
      style={{ gap: Utils.Token.ToInt(Spacing, 'stack.nano') }}>
      {tokenInputs}
    </Box>
  );
};
